import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';
import { FormikValues } from 'formik';

import api from '../../utils/api';

const parseBooleanValue = (value: unknown) => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }

  return Boolean(value);
};

export const useCreateUpdateRequiredDocumentMutation = (handleClose?: () => void) => {
  const queryClient = useQueryClient();

  const createUpdateRequiredDocument = async (payload: RequiredDocumentPayload): Promise<RequiredDocumentMutationResponse> => {
    return (await api.post('/AdmissionDocument/Admin/CreateUpdateRequiredDocument', payload))?.data;
  };

  const mutation = useMutation({
    mutationKey: ['create-update-required-document'],
    mutationFn: createUpdateRequiredDocument,
  });

  const createUpdateRequiredDocumentHandler = async (
    values: FormikValues,
    resetForm?: () => void,
  ) => {
    const payload: RequiredDocumentPayload = {
      name: values?.name ?? '',
      maxFileSizeInMB: Number(values?.maxFileSizeInMB ?? 0),
      allowedFileTypes: values?.allowedFileTypes ?? '',
      isRequired: parseBooleanValue(values?.isRequired),
      programTypeId: Number(values?.programTypeId ?? values?.programType ?? 0),
      description: values?.description ?? '',
      id: Number(values?.id ?? 0),
    };

    try {
      await mutation.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({
            message: data?.message || 'Required document saved successfully',
          });

          queryClient.refetchQueries({
            queryKey: ['get-required-documents'],
          });

          resetForm?.();
          handleClose?.();
        },
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || error?.response?.data?.title || 'Failed to save required document',
      });
    }
  };

  return {
    createUpdateRequiredDocumentHandler,
    isLoading: mutation.isPending,
  };
};
